import { useState, useMemo } from 'react';
import { IconButton, Table, TableRow, TableHeadCell, TableCell, Pagination } from '../../../components/Common';
import { useTranslation } from '../../../hooks/useTranslation';
import { ProjectDtoRelV2 } from '../../../commonTypes/project.types';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../../store/slices/auth.slice';
import { RoleTypeEnum } from '../../../commonTypes/user.types';
import { formatToMonthDayYear } from '../../../util/date/dateFormater';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import { MemberAvatars } from './MemberAvatars';

interface ProjectsTableProps {
    projects: ProjectDtoRelV2[];
    searchTerm: string;
    selectedFilters: string[];
}

export const ProjectsTable = ({ projects, searchTerm, selectedFilters }: ProjectsTableProps) => {
    const { t } = useTranslation('projects');
    const navigate = useNavigate();
    const authUser = useSelector(selectAuth);
    const [page, setPage] = useState(0);
    const [elementsPerPage, setElementsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<'NAME' | 'CLIENT' | 'START_DATE' | 'END_DATE' | 'STATUS'>('NAME');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

    const canEdit =
        authUser.role.name !== RoleTypeEnum.ORDINARY && authUser.role.name !== RoleTypeEnum.BASIC && authUser.role.name !== RoleTypeEnum.TEAM_LEAD;

    const startIndex = page * elementsPerPage;
    const endIndex = startIndex + elementsPerPage;

    const handleSort = (column: typeof sortBy) => {
        setSortDirection(prev => (column === sortBy ? (prev === 'ASC' ? 'DESC' : 'ASC') : 'ASC'));
        setSortBy(column);
    };

    const filtered = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();

        let result = !q
            ? [...projects]
            : projects.filter(p => {
                  const n = p.name?.toLowerCase() ?? '';
                  const c = p.clientName?.toLowerCase() ?? '';
                  return n.includes(q) || c.includes(q);
              });

        if (selectedFilters.length > 0) {
            result = result.filter(project => {
                const projectStatus = project.projectStatus || (project.active ? 'InProgress' : 'Draft');
                return selectedFilters.includes(projectStatus);
            });
        }

        return result;
    }, [projects, searchTerm, selectedFilters]);

    const sorted = useMemo(() => {
        const res = [...filtered];

        res.sort((a, b) => {
            const dir = sortDirection === 'ASC' ? 1 : -1;
            const toTime = (v: string | undefined) => (v ? new Date(v).getTime() : 0);

            switch (sortBy) {
                case 'NAME': {
                    return dir * a.name.localeCompare(b.name);
                }
                case 'CLIENT': {
                    return dir * (a.clientName || '').localeCompare(b.clientName || '');
                }
                case 'START_DATE': {
                    return dir * (toTime(a.startDate) - toTime(b.startDate));
                }
                case 'END_DATE': {
                    return dir * (toTime(a.endDate) - toTime(b.endDate));
                }
                case 'STATUS': {
                    return dir * (a.projectStatus || '').localeCompare(b.projectStatus || '');
                }
                default:
                    return 0;
            }
        });

        return res;
    }, [filtered, sortBy, sortDirection]);

    const paginatedProjects = sorted.slice(startIndex, endIndex);

    const handleViewProject = (projectId: string) => {
        navigate(`/project/${projectId}`);
    };

    const handleEditProject = (projectId: string) => {
        navigate(`/projects/${projectId}/edit`);
    };

    const getArrowClass = (column: typeof sortBy) => {
        if (sortBy !== column) return 'invoices__header-cell--sortable__arrow';
        return `invoices__header-cell--sortable__arrow invoices__header-cell--sortable__arrow--${sortDirection.toLowerCase()}`;
    };

    return (
        <div className="projects-table">
            <div className="projects-table__wrapper">
                <Table className="invoices__table">
                    <TableRow className="invoices__header-cell--background">
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                {t('project')}
                                <img
                                    src={sortBy === 'NAME' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('NAME')}
                                    onClick={() => handleSort('NAME')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Client
                                <img
                                    src={sortBy === 'CLIENT' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('CLIENT')}
                                    onClick={() => handleSort('CLIENT')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell">{t('members')}</div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Start Date
                                <img
                                    src={sortBy === 'START_DATE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('START_DATE')}
                                    onClick={() => handleSort('START_DATE')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                End Date
                                <img
                                    src={sortBy === 'END_DATE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('END_DATE')}
                                    onClick={() => handleSort('END_DATE')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                {t('status')}
                                <img
                                    src={sortBy === 'STATUS' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('STATUS')}
                                    onClick={() => handleSort('STATUS')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background"></TableHeadCell>
                    </TableRow>

                    {paginatedProjects.map((project, index) => {
                        const activeMembers = project.projectAssignees?.filter(assignee => assignee.active) || [];

                        return (
                            <TableRow
                                key={project.id}
                                className={`invoices__row ${index % 2 === 0 ? 'invoices__row--white' : 'invoices__row--gray'}`}
                            >
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">{project.name}</span>
                                    </div>
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">{project.clientName || '-'}</span>
                                    </div>
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    {activeMembers.length > 0 ? (
                                        <MemberAvatars members={activeMembers} maxDisplay={3} />
                                    ) : (
                                        <span className="invoices__cell-text">-</span>
                                    )}
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">
                                            {project.startDate ? formatToMonthDayYear(project.startDate) : '-'}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">{project.endDate ? formatToMonthDayYear(project.endDate) : '-'}</span>
                                    </div>
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <ProjectStatusBadge status={project.projectStatus || (project.active ? 'InProgress' : 'Draft')} />
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <div className="project-table__actions-cell">
                                        {canEdit && (
                                            <IconButton
                                                onClick={() => handleEditProject(project.id)}
                                                iconUrl="/svg/pen-grey.svg"
                                                iconAlt="Edit Project"
                                                variant="icon"
                                                type="button"
                                            />
                                        )}
                                        <IconButton
                                            onClick={() => handleViewProject(project.id)}
                                            iconUrl="/svg/eye-grey.svg"
                                            iconAlt="View Project"
                                            variant="icon"
                                            type="button"
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {sorted.length > 10 && (
                        <TableRow className="projects-table__pagination-row">
                            <TableCell colSpan={7} className="projects-table__pagination-cell">
                                <Pagination
                                    total={sorted.length}
                                    elementsPerPage={elementsPerPage}
                                    setElementsPerPage={setElementsPerPage}
                                    page={page}
                                    setPage={setPage}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </Table>
            </div>
        </div>
    );
};
